import { useBulkDeleteTaskMutation, useBulkUpdateTaskDetailsMutation, useFetchAllFilterDataQuery } from 'api/taskApi';
import {
    Check,
    Chrome,
    Compass,
    Copy,
    Globe,
    Info,
    Link,
    Monitor,
    Trash2,
    User
} from 'lucide-react';
import Firefox from 'media/svgs/firefox.svg?react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TaskAttributes } from 'types/api/tasks';
import { Button, Dialog, DropdownMenu, Label, MultiSelect, Tooltip } from 'ui';
import { Option } from 'ui/components/MultiSelect';
import { cn } from 'utils/cn';
import { getSelectedSiteCollaborateInfo, getSelectedSiteInfo } from '../../../../../store/siteInfoSlice';
import { setCreateModalOpen, setGeneralTaskModalOpen, setSelectedTaskId } from '../../../../../store/tasksSlice';
import { getUser } from '../../../../../store/userSlice';

// Separated Dialog Component
function DeleteTaskDialog({
    taskDetails,
    isOpen,
    setIsOpen
}: {
    taskDetails: Partial<TaskAttributes>,
    isOpen: boolean,
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
    const { t } = useTranslation();
    const [deleteTask, { isLoading: isLoadingBulkDelete }] = useBulkDeleteTaskMutation();
    const dispatch = useDispatch();

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <Dialog.Portal>
                <Dialog.Overlay />
                <Dialog.Content container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')} className='z-50'>
                    <Dialog.Header>
                        <Dialog.Title>
                            {t('tech_info.delete_dialog.title', 'Delete Task')}
                        </Dialog.Title>
                    </Dialog.Header>
                    <Dialog.Description className="flex flex-col gap-2 p-0">
                        <p>
                            {t('tech_info.delete_dialog.confirm', 'Are you sure you want to delete this task?')}
                        </p>
                        <p>
                            {t('tech_info.delete_dialog.warning', 'This will remove all the comments and attachments associated with this task.')}
                        </p>
                        <p className="text-destructive">{t('tech_info.delete_dialog.permanent', 'This action cannot be undone.')}</p>
                    </Dialog.Description>
                    <Dialog.Footer className="flex px-0 pb-2 gap-2">
                        <Dialog.Close asChild className='m-0'>
                            <Button variant="secondary" size="sm" className='ml-0'>
                                {t('tech_info.buttons.cancel', 'Cancel')}
                            </Button>
                        </Dialog.Close>
                        <Button
                            variant="destructive"
                            size="sm"
                            loading={isLoadingBulkDelete}
                            onClick={async () => {
                                if (!taskDetails) return;
                                await deleteTask({
                                    taskId: taskDetails.task_id!,
                                    websiteId: taskDetails.site_id!,
                                }).then(() => {
                                    dispatch(setCreateModalOpen(false));
                                    dispatch(setGeneralTaskModalOpen(false));
                                    dispatch(setSelectedTaskId(null));
                                    setIsOpen(false);
                                });
                            }}
                        >
                            {t('tech_info.buttons.confirm_delete', 'Delete Task')}
                        </Button>
                    </Dialog.Footer>
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog>
    );
}

function DeleteTaskButton({
    taskDetails,
    setPopoverOpen,
    setIsDialogOpen
}: {
    taskDetails: Partial<TaskAttributes>,
    setPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>,
    setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>
}) {
    const { t } = useTranslation();
    const user = useSelector(getUser);
    const [_, { isLoading: isLoadingBulkDelete }] = useBulkDeleteTaskMutation();

    const allowedRoles = ['contributor', 'admin', 'manager'];
    const disabled = isLoadingBulkDelete || !taskDetails || !allowedRoles.includes(user?.role);

    return (
        <Button
            tooltip={t('tech_info.tooltip.delete', 'Delete task')}
            className="hover:text-white"
            variant="muted-destructive"
            size="sm"
            leftIcon={<Trash2 size={18} aria-hidden="true" />}
            disabled={disabled}
            loading={isLoadingBulkDelete}
            onClick={() => {
                setPopoverOpen(false);
                setIsDialogOpen(true);
            }}
        >
            {t('tech_info.buttons.delete', 'Delete task')}
        </Button>
    );
}

export default function TechInfo({
    infoMap,
    taskDetails
}: {
    infoMap: Record<string, string | number>;
    taskDetails: Partial<TaskAttributes>;
}) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [value, setValue] = useState<Option[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const collaborateInfo = useSelector(getSelectedSiteCollaborateInfo)
    const siteInfo = useSelector(getSelectedSiteInfo)
    const { data: filterData } = useFetchAllFilterDataQuery(siteInfo?.workspace_id || '');
    const [updateTaskTags] =
        useBulkUpdateTaskDetailsMutation();

    const findBrowser = (browser: string | number) => {
        if (!browser || typeof browser !== 'string') return 'unknown';
        if (browser.toLowerCase().includes('chrome')) return 'chrome';
        else if (browser.toLowerCase().includes('edge')) return 'edge';
        else if (browser.toLowerCase().includes('firefox')) return 'firefox';
        else if (browser.toLowerCase().includes('safari')) return 'safari';
        else return 'unknown';
    };
    const hasPermission = useMemo(() => {
        return collaborateInfo?.result?.role === 'admin' || collaborateInfo?.result?.role === 'contributor';
    }, [collaborateInfo?.result?.role]);

    const [copiedTask, setCopiedTask] = useState<boolean>(false);
    const [copiedPage, setCopiedPage] = useState<boolean>(false);

    const handleCopyTask = async () => {
        try {
            await navigator.clipboard.writeText(infoMap.shareTask as string);
            setCopiedTask(true);
            setTimeout(() => setCopiedTask(false), 1500);
        } catch (err) {
            console.error(t('tech_info.errors.copy_failed', 'Failed to copy text: '), err);
        }
    };

    const handleCopyPage = async () => {
        try {
            await navigator.clipboard.writeText(infoMap.pageURL as string);
            setCopiedPage(true);
            setTimeout(() => setCopiedPage(false), 1500);
        } catch (err) {
            console.error(t('tech_info.errors.copy_failed', 'Failed to copy text: '), err);
        }
    };

    const options = useMemo(() => {
        if (!filterData?.task_tags) return [];

        const selectedValues = new Set(value.map((v) => v.label));

        return filterData?.task_tags
            .filter((tag) => !selectedValues.has(tag.label))
            .map((tag) => ({
                label: tag.label,
                value: tag.label.toString(),
                id: tag.id,
            }));
    }, [filterData, value]);

    useEffect(() => {
        if (taskDetails) {
            setValue(
                taskDetails?.task_tags?.map((tag) => ({
                    label: tag.value,
                    value: tag.value,
                    id: tag.id,
                })) || []
            );
        }
    }, [taskDetails]);

    const handleOnChange = useCallback(
        (tags: Option[]) => {
            if (!taskDetails) return;

            const currentTags = taskDetails?.task_tags?.map((tag) => ({
                id: tag.id,
                value: tag.value,
            }));
            const newTags = tags.map((tag) => tag.label);

            // Find tags to add and remove
            const tagsToAdd = newTags.filter(
                (tag) => !currentTags?.some((ct) => ct.value === tag)
            );
            const tagsToRemove = currentTags?.filter(
                (tag) => !newTags.includes(tag.value)
            );

            // Handle tag addition
            if (tagsToAdd.length > 0) {
                const newTag = tagsToAdd[0]; // Add one tag at a time
                if (currentTags?.some((tag) => tag.value === newTag)) {
                    //   toast.error('Tag already exists');
                    // Clean up the added tag
                    setValue((prevValue) => prevValue.filter((t) => t.label !== newTag));
                    return;
                }
                updateTaskTags({
                    updateMethod: 'task_tags',
                    taskId: taskDetails.task_id!,
                    websiteId: taskDetails.site_id!,
                    value: newTag,
                    is_delete: false,
                });
            }

            // Handle tag removal
            if (tagsToRemove && tagsToRemove.length > 0) {
                const tagToRemove = tagsToRemove[0];
                updateTaskTags({
                    updateMethod: 'task_tags',
                    websiteId: taskDetails.site_id!,
                    taskId: taskDetails.task_id!,
                    id: tagToRemove.id, // Use the id for deletion
                    is_delete: true,
                });
            }
        },
        [taskDetails, updateTaskTags]
    );

    const browserIcons = {
        chrome: <Chrome size={18} aria-hidden="true" />,
        firefox: <Firefox width={18} height={18} size={18} aria-hidden="true" />,
        safari: <Compass size={18} aria-hidden="true" />,
        unknown: <Globe size={18} aria-hidden="true" />,
    };

    const noWorkspaceTag = filterData?.task_tags?.length === 0

    return (
        <>
            <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
                <DropdownMenu.Trigger asChild>
                    <Button
                        tooltip={t('tech_info.tooltip.main', 'Technical Information')}
                        variant="ghost"
                        size="icon"
                    >
                        <Info size={18} aria-hidden="true" />
                    </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content
                    portal={true}
                    container={document.getElementById('root-atarim')?.shadowRoot?.getElementById('app-wrapper')}
                    align="start"
                    className="flex flex-col gap-3 p-3 min-w-[315px] w-full max-w-[350px] pointer-events-auto"
                >
                    <div className='flex flex-col gap-2'>
                        <Label>{t('tech_info.labels.tags', 'Add Tags')}</Label>
                        <MultiSelect
                            creatable
                            options={options}
                            onChange={handleOnChange}
                            value={value}
                            hideClearAllButton
                            placeholder={t('tech_info.placeholders.tags', 'Type and press enter to create a new tag')}
                            loadingIndicator={
                                <p className="text-muted-foreground py-2 text-center text-lg leading-10">
                                    {t('tech_info.status.loading', 'loading...')}
                                </p>
                            }
                            emptyIndicator={
                                <p className="text-muted-foreground w-full text-center text-sm leading-10">
                                    {
                                        noWorkspaceTag ?
                                            t('tech_info.status.create_tag', 'Create your first tag.') :
                                            t('tech_info.status.no_results', 'No results found.')

                                    }
                                </p>
                            }
                        />
                    </div>
                    <div className='flex flex-col gap-2'>
                        <Label className='my-1'>{t('tech_info.labels.details', 'Technical Details')}</Label>
                        <div className="flex flex-col gap-2 text-sm">
                            <p className="inline-flex items-center gap-2">
                                <Tooltip content={t('tech_info.tooltip.screen_size', 'Screen Size')} >
                                    <Monitor size={18} aria-hidden="true" />
                                </Tooltip>
                                {infoMap.screenSize}
                            </p>
                            <p className="inline-flex items-center gap-2">
                                <Tooltip content={t('tech_info.tooltip.browser', 'Browser')} >
                                    {browserIcons[findBrowser(infoMap.browser) as keyof typeof browserIcons]}
                                </Tooltip>
                                <span className='capitalize'>{infoMap.browser}</span>
                            </p>
                            <p className="inline-flex items-center gap-2">
                                <Tooltip content={t('tech_info.tooltip.created_by', 'Created By')} >
                                    <User size={18} aria-hidden="true" />
                                </Tooltip>
                                {infoMap.createdBy}
                            </p>
                            <p className="inline-flex items-center gap-2">
                                <span className="flex h-[18px] w-[18px] items-center justify-center">
                                    <Tooltip content={t('tech_info.tooltip.task_id', 'Task ID')} >
                                        <span className="border-textcolor block size-4 rounded-[15%_50%_50%_50%] border-2"></span>
                                    </Tooltip>
                                </span>
                                {infoMap.taskID}
                            </p>
                            <Label className='mb-1 mt-4'>{t('tech_info.labels.web_page_details', 'Web Page Details')}</Label>
                            <p className="inline-flex items-center gap-2">
                                <Tooltip content={t('tech_info.tooltip.site_name', 'Website Name')}>
                                    <Globe size={18} className='shrink-0' aria-hidden="true" />
                                </Tooltip>
                                <span className='break-all'>{infoMap.siteName}</span>
                            </p>
                            <p className="inline-flex items-center gap-2">
                                <Tooltip content={t('tech_info.tooltip.task_page_name', 'Page Name')}>
                                    <Link size={18} className='shrink-0' aria-hidden="true" />
                                </Tooltip>
                                {infoMap.taskPageName}
                            </p>
                            <p className="inline-flex items-center gap-2">
                                <Tooltip content={t('tech_info.tooltip.page_url', 'Page URL')}>
                                    <Globe size={18} className='shrink-0' aria-hidden="true" />
                                </Tooltip>
                                <span className='break-all truncate w-full'>{infoMap.pageURL}</span>
                                <Button
                                    onClick={handleCopyPage}
                                    tooltip={copiedPage ? t('tech_info.aria.copied', 'Copied') : t('tech_info.tooltip.pagrurl.copy', 'Click to copy page url')}
                                    variant="ghost"
                                    size="sm"
                                    aria-label={copiedPage ? t('tech_info.aria.copied', 'Copied') : t('tech_info.aria.copy', 'Copy to clipboard')}
                                    className="disabled:opacity-100 disabled:cursor-default hover:bg-transparent -my-2 w-8 h-8"
                                    disabled={copiedPage}
                                >
                                    <div
                                        className={cn(
                                            "transition-all duration-200",
                                            copiedPage ? "scale-100 opacity-100" : "scale-0 opacity-0",
                                        )}
                                    >
                                        <Check className="text-primary" size={16} strokeWidth={2} aria-hidden="true" />
                                    </div>
                                    <div
                                        className={cn(
                                            "absolute transition-all",
                                            copiedPage ? "scale-0 opacity-0" : "scale-100 opacity-100",
                                        )}
                                    >
                                        <Copy size={16} strokeWidth={2} aria-hidden="true" />
                                    </div>
                                </Button>
                            </p>

                        </div>
                    </div>
                    <div className='flex gap-2 mt-2'>
                        {taskDetails && hasPermission && (
                            <DeleteTaskButton
                                taskDetails={taskDetails}
                                setPopoverOpen={setOpen}
                                setIsDialogOpen={setIsDialogOpen}
                            />
                        )}
                        <Button
                            tooltip={copiedTask ? t('tech_info.aria.copied', 'Copied') : t('tech_info.tooltip.tasklink.copy', 'Click to copy task link')}
                            onClick={handleCopyTask}
                            disabled={copiedTask}
                            size="sm"
                            variant="outline"
                            className=" disabled:cursor-default hover:bg-transparent w-full border-primary hover:bg-primary/10"
                            aria-label={copiedTask ? t('tech_info.aria.copied', 'Copied') : t('tech_info.aria.copy', 'Copy to clipboard')}
                        >
                            <div
                                className={cn(
                                    "transition-all duration-200 flex flex-row items-center gap-1",
                                    copiedTask ? "scale-100 opacity-100" : "scale-0 opacity-0",
                                )}
                            >
                                <Check className="text-primary" size={16} strokeWidth={2} aria-hidden="true" />
                                Copied
                            </div>
                            <div
                                className={cn(
                                    "absolute transition-all flex flex-row items-center gap-1",
                                    copiedTask ? "scale-0 opacity-0" : "scale-100 opacity-100",
                                )}
                            >
                                <Copy size={16} strokeWidth={2} aria-hidden="true" />
                                Copy task link
                            </div>
                        </Button>
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu>

            {taskDetails && (
                <DeleteTaskDialog
                    taskDetails={taskDetails}
                    isOpen={isDialogOpen}
                    setIsOpen={setIsDialogOpen}
                />
            )}
        </>
    );
}